import { useEffect } from "react";
import { I18nextProvider, useTranslation } from "react-i18next";
import i18n from "./i18n";
import { syncDocument } from "./i18n/syncDocument";
import { fixedEsI18n } from "./i18n/fixedEs";
import { findRoute } from "./routes";
import { RouteContext } from "./seo/RouteContext";
import { applyHead } from "./seo/applyHead";
import { SITE_URL } from "./seo/site";
import Navbar from "./components/layout/Navbar";
import Footer from "./components/layout/Footer";
import EmailButton from "./components/layout/EmailButton";
import QuickBar from "./components/layout/QuickBar";
import HomePage from "./pages/HomePage";
import NotFoundPage from "./pages/NotFoundPage";
import SeoPage from "./pages/SeoPage";

const PAGES = {
  home: HomePage,
  seo: SeoPage,
  notFound: NotFoundPage,
};

function Layout({ route }) {
  const { t, i18n } = useTranslation();
  const isHome = route.page === "home";
  const Page = PAGES[route.page] || NotFoundPage;

  // Home: keep <html lang>, the tab title and the description/social meta
  // tags in the language the visitor is reading. Other pages are Spanish-only
  // and carry their own title/description.
  useEffect(() => {
    if (isHome) syncDocument(i18n.resolvedLanguage || i18n.language, t);
    else applyHead(route, `${SITE_URL}${route.path}`);
  }, [isHome, route, i18n.resolvedLanguage, i18n.language, t]);

  return (
    <RouteContext.Provider value={{ path: route.path, isHome, localSections: route.localSections || [] }}>
      <a href="#main" className="skip-link">
        {t("a11y.skipToContent")}
      </a>
      <Navbar />
      <main id="main">
        <Page route={route} />
      </main>
      <Footer />
      <EmailButton />
      <QuickBar />
    </RouteContext.Provider>
  );
}

function App({ path, switchLanguageTo }) {
  const route = findRoute(path ?? (typeof window !== "undefined" ? window.location.pathname : "/"));

  // After hydrating the Spanish prerender, move to the visitor's chosen
  // language. Runs after every child effect, so react-i18next is listening.
  useEffect(() => {
    if (switchLanguageTo) i18n.changeLanguage(switchLanguageTo);
  }, [switchLanguageTo]);

  if (route.page === "home") return <Layout route={route} />;
  return (
    <I18nextProvider i18n={fixedEsI18n}>
      <Layout route={route} />
    </I18nextProvider>
  );
}

export default App;
